import { CanAccess, EditBase, Form, useEditContext } from "ra-core";
import { Navigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";

import { FormToolbar } from "../layout/FormToolbar";
import type { Client } from "../types";
import { ClientInputs } from "./ClientInputs";
import { cleanupClientForSave } from "./clientValidation";

/**
 * Reachable from the client show page; lands back on the show page on save
 * (`redirect="show"`), per the ticket's acceptance criteria.
 *
 * The form is gated by `canAccess` so a role without edit access can't reach
 * it by typing the URL directly (react-admin's <Resource edit=.../> route
 * itself is not access-gated). The guard lives inside `EditBase` (Almikia
 * Epic E3b, TASK-065): `useEditController` runs its own access check before
 * the record is fetched, so a check placed outside `EditBase` always passes
 * a lider through to a team member's edit form -- the record only exists
 * once `EditBase`'s `RecordContext` is populated.
 */
export const ClientEdit = () => (
  <EditBase
    actions={false}
    redirect="show"
    transform={(values: Partial<Client>) => cleanupClientForSave(values)}
  >
    <ClientEditContent />
  </EditBase>
);

const ClientEditContent = () => {
  const { isPending, record } = useEditContext<Client>();
  if (isPending || !record) return null;
  return (
    <CanAccess
      resource="clients"
      action="edit"
      accessDenied={<Navigate to={`/clients/${record.id}/show`} replace />}
    >
      <div className="mt-2 max-w-lg mx-auto">
        <Card>
          <CardContent>
            <Form className="space-y-4 pt-4">
              <ClientInputs />
              <FormToolbar />
            </Form>
          </CardContent>
        </Card>
      </div>
    </CanAccess>
  );
};
